
import React, { useState, useEffect, useMemo } from 'react';
import { adminTradingService, AdminAggregatedTrade } from '../../services/admin.trading.service';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Users,
  BarChart3,
  PieChart as PieChartIcon,
  Search,
  ArrowUpDown,
  Filter,
  Calendar,
  Layers,
  Target,
  Loader2
} from 'lucide-react';
import Select from '../../components/common/Select';

const AdminReports: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [ledger, setLedger] = useState<AdminAggregatedTrade[]>([]);

  // State for Table
  const [searchQuery, setSearchQuery] = useState('');
  const [assetFilter, setAssetFilter] = useState('ALL');
  const [sortConfig, setSortConfig] = useState<{ key: keyof AdminAggregatedTrade, direction: 'asc' | 'desc' } | null>({ key: 'netPnl', direction: 'desc' });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await adminTradingService.getTradingLedger();
      if (Array.isArray(data)) {
        setLedger(data);
      }
    } catch (error) {
      console.error('Failed to fetch admin ledger:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Derived Analytics
  const summary = useMemo(() => {
    let totalInvestment = 0;
    let totalPayout = 0;
    let totalWins = 0;
    let totalLosses = 0;

    ledger.forEach(user => {
      totalInvestment += parseFloat(user.aggregatedPrincipal || '0');
      totalPayout += parseFloat(user.finalSettlement || '0');

      user.trades?.forEach(t => {
        if (t.outcome === 'PROFIT') totalWins++;
        else totalLosses++;
      });
    });

    const netProfit = totalInvestment - totalPayout;
    const totalTrades = totalWins + totalLosses;
    const winRatio = totalTrades > 0 ? Math.round((totalWins / totalTrades) * 100) : 0;

    return { totalInvestment, totalPayout, netProfit, winRatio };
  }, [ledger]);

  // Asset Distribution
  const assetDist = useMemo(() => {
    const counts: Record<string, number> = {};
    ledger.forEach(user => {
      user.trades?.forEach(t => {
        counts[t.asset] = (counts[t.asset] || 0) + parseFloat(t.principal || '0');
      });
    });
    return Object.entries(counts).map(([name, volume]) => ({ name, volume }));
  }, [ledger]);

  // Outcome Integrity
  const outcomeDist = useMemo(() => {
    let wins = 0;
    let losses = 0;
    ledger.forEach(user => {
      user.trades?.forEach(t => {
        if (t.outcome === 'PROFIT') wins++;
        else losses++;
      });
    });
    return [
      { name: 'Profitable', value: wins, color: '#FFCE1B' },
      { name: 'Mitigated/Loss', value: losses, color: '#262626' }
    ];
  }, [ledger]);

  const handleSort = (key: any) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedUsers = useMemo(() => {
    let result = [...ledger];

    // Filter by Search
    if (searchQuery) {
      result = result.filter(u =>
        u.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.userId?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by Asset (Using topAsset logic or any trade inclusion)
    if (assetFilter !== 'ALL') {
      result = result.filter(u => u.trades?.some(t => t.asset === assetFilter));
    }

    // Sort
    if (sortConfig) {
      result.sort((a: any, b: any) => {
        const aValue = parseFloat(a[sortConfig.key] || '0');
        const bValue = parseFloat(b[sortConfig.key] || '0');
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [ledger, searchQuery, assetFilter, sortConfig]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-brand-primary" size={48} />
        <p className="text-brand-muted text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Aggregating Platform Intelligence...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-24">

      {/* 1. Header & Global Filters */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase italic">Platform Analytics</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Global Liquidity & Trader Performance Analysis</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-brand-card border border-brand-border p-1 rounded-xl">
            <button className="px-4 py-2 text-[10px] font-black uppercase tracking-widest bg-brand-primary text-black rounded-lg">Real-Time Sync</button>
          </div>
        </div>
      </section>

      {/* 2. Platform Summary Metrics */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          label="Total Investment Volume"
          value={`$${summary.totalInvestment.toLocaleString()}`}
          icon={<Layers size={20} />}
          subValue="Live Cross-platform"
        />
        <SummaryCard
          label="Settlement Outflow"
          value={`$${summary.totalPayout.toLocaleString()}`}
          icon={<TrendingUp size={20} />}
          color="text-emerald-500"
          subValue="User Payouts"
        />
        <SummaryCard
          label="Net Platform Profit"
          value={`$${summary.netProfit.toLocaleString()}`}
          icon={<Target size={20} />}
          color="text-brand-primary"
          subValue="Revenue Efficiency"
        />
        <SummaryCard
          label="Global Win Ratio"
          value={`${summary.winRatio}%`}
          icon={<Activity size={20} />}
          subValue="System Edge"
        />
      </section>

      {/* 3. Interactive Analytics Panels */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Asset Distribution */}
        <div className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <BarChart3 size={20} className="text-brand-primary" />
              Volume Distribution by Asset
            </h3>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={assetDist}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#262626" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8E8E8E', fontSize: 11, fontWeight: 'bold' }}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: '12px' }}
                  itemStyle={{ color: '#FFCE1B', fontSize: '11px' }}
                  cursor={{ fill: 'rgba(255, 206, 27, 0.05)' }}
                />
                <Bar
                  dataKey="volume"
                  fill="#FFCE1B"
                  radius={[6, 6, 0, 0]}
                  barSize={60}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Win/Loss Outcome Ratio */}
        <div className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <PieChartIcon size={20} className="text-brand-primary" />
              Outcome Integrity Matrix
            </h3>
          </div>
          <div className="h-64 w-full relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={outcomeDist}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {outcomeDist.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: '12px' }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => <span className="text-[10px] font-black text-brand-muted uppercase tracking-widest">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mb-10">
              <span className="text-[10px] text-brand-muted uppercase font-black tracking-widest">Ratio</span>
              <span className="text-xl font-black text-white">{summary.winRatio}%</span>
            </div>
          </div>
        </div>
      </section>

      {/* 4. User Performance Ledger */}
      <section className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-2">
          <div>
            <h3 className="text-xl font-bold text-white uppercase tracking-tight">User Performance Ledger</h3>
            <p className="text-[10px] text-brand-muted uppercase font-bold tracking-[0.2em] mt-1">Granular analysis of individual trader profitability</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" size={14} />
              <input
                type="text"
                placeholder="Search Identity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-brand-card border border-brand-border rounded-xl pl-10 pr-4 py-2 text-xs text-white focus:outline-none focus:border-brand-primary"
              />
            </div>
            <Select
              options={[
                { value: 'ALL', label: 'All Platforms' },
                { value: 'BTC', label: 'BTC Core' },
                { value: 'ETH', label: 'Ethereum' },
                { value: 'USDT', label: 'USDT Tether' }
              ]}
              value={assetFilter}
              onChange={(e) => setAssetFilter(e.target.value)}
              className="!w-full sm:!w-40 !py-2 !text-[10px]"
            />
          </div>
        </div>

        <div className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-brand-dark/40 border-b border-brand-border">
                  <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Trader</th>
                  <SortableHeader label="Executions" sortKey="executionsCount" config={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Principal" sortKey="aggregatedPrincipal" config={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Settlement" sortKey="finalSettlement" config={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Net Delta" sortKey="netPnl" config={sortConfig} onSort={handleSort} />
                  <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] text-right">Top Protocol</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {filteredAndSortedUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-brand-primary/5 transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-dark border border-brand-border flex items-center justify-center text-[10px] font-black text-brand-muted">
                          {user.userName?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">{user.userName}</p>
                          <p className="text-[9px] text-brand-muted font-mono opacity-60 tracking-tighter">{user.userId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 font-mono text-xs text-white">{user.executionsCount}</td>
                    <td className="px-6 py-5 font-mono text-xs text-white">${parseFloat(user.aggregatedPrincipal || '0').toLocaleString()}</td>
                    <td className="px-6 py-5 font-mono text-xs text-white">${parseFloat(user.finalSettlement || '0').toLocaleString()}</td>
                    <td className="px-6 py-5">
                      <span className={`font-mono text-xs font-black ${parseFloat(user.netPnl || '0') >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {parseFloat(user.netPnl || '0') >= 0 ? '+' : ''}${parseFloat(user.netPnl || '0').toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <span className="text-[9px] font-black text-brand-primary border border-brand-primary/20 px-2 py-0.5 rounded bg-brand-primary/5 uppercase tracking-widest">
                        {user.trades?.[0]?.asset || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

const SummaryCard: React.FC<{ label: string, value: string, icon: React.ReactNode, color?: string, subValue?: string }> = ({ label, value, icon, color = 'text-white', subValue }) => (
  <div className="bg-brand-card border border-brand-border p-6 rounded-3xl hover:border-brand-primary/30 transition-all duration-300 group shadow-lg">
    <div className="text-brand-muted mb-4 p-2 bg-brand-dark w-fit rounded-xl border border-brand-border group-hover:text-brand-primary transition-colors">
      {icon}
    </div>
    <p className="text-[9px] text-brand-muted uppercase font-black tracking-[0.2em] mb-1">{label}</p>
    <div className="flex items-baseline justify-between gap-2">
      <h3 className={`text-2xl font-bold tracking-tighter ${color}`}>{value}</h3>
      {subValue && <span className="text-[9px] font-black text-brand-muted uppercase opacity-60 tracking-widest">{subValue}</span>}
    </div>
  </div>
);

const SortableHeader: React.FC<{
  label: string,
  sortKey: string,
  config: { key: any, direction: 'asc' | 'desc' } | null,
  onSort: (key: any) => void
}> = ({ label, sortKey, config, onSort }) => {
  const isActive = config?.key === sortKey;
  return (
    <th
      className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] cursor-pointer hover:text-white transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={`transition-opacity ${isActive ? 'opacity-100 text-brand-primary' : 'opacity-20'}`} />
      </div>
    </th>
  );
};

export default AdminReports;
